import { useCallback, createContext, useContext, useState } from 'react'
import {
  QUERY_GET_CONTENT_BY_SLUG,
  QUERY_GET_STORE_BY_SLUG,
  QUERY_GET_SLERP_CART,
  QUERY_GET_SLERP_MERCHANT,
  MUTATE_UPDATE_CUSTOMER_ID
} from 'shop/client'
import {
  AppContentQueryResponse,
  MerchantStoreQueryResponse,
  SlerpCartQueryResponse,
  SlerpMerchantQueryResponse
} from './types'
import { useShop } from './useShop'
import { useCart } from '../useCart'
import { Cart } from 'shop/components'
import { isEqual } from 'lodash'
import { returnShortenedCart } from 'shop/utils/cart'
import { useSlerpCart } from './useSlerpCart'

type LoaderState = {
  fetchAppContent: () => Promise<AppContentQueryResponse | null>
  fetchMerchantStore: (
    storeSlug: string | null
  ) => Promise<MerchantStoreQueryResponse | null>
  fetchSlerpCart: (cart: Cart | null) => Promise<SlerpCartQueryResponse | null>
  fetchSlerpMerchant: () => Promise<SlerpMerchantQueryResponse | null>
  updateCustomerId: (customerId: string) => Promise<{ id: string } | null>
}

export const SetupLoader = (): LoaderState => {
  const { useShopClient, config } = useShop()
  const { setIsSlerpCartLoading } = useSlerpCart()
  const { cartId } = useCart()

  const { domain: merchantSlug } = config
  const client = useShopClient()

  const [merchantStoreCache, setMerchantStoreCache] = useState<{
    [key: string]: MerchantStoreQueryResponse | null
  }>({})

  const [cartStoreCache, setCartStoreCache] = useState<{
    [key: string]: [cart: any, response: SlerpCartQueryResponse | null]
  }>({})

  const fetchAppContent = useCallback(async () => {
    if (!merchantSlug || !client) {
      return null
    }

    try {
      const result = await client.query({
        query: QUERY_GET_CONTENT_BY_SLUG,
        variables: {
          merchantSlug: merchantSlug
        }
      })

      const appContent = result?.data?.getContent

      if (!appContent) {
        return null
      }

      return appContent
    } catch {
      return null
    }

    // [client] causes endless api calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantSlug])

  const fetchMerchantStore = useCallback(async (storeSlug) => {
    if (!storeSlug) {
      return null
    }

    if (merchantStoreCache[storeSlug]) {
      return merchantStoreCache[storeSlug]
    }

    try {
      const result = await client.query({
        query: QUERY_GET_STORE_BY_SLUG,
        variables: {
          storeSlug: storeSlug
        }
      })

      const merchantStore = result?.data?.getStore
      setMerchantStoreCache(merchantStore)

      if (!merchantStore) {
        return null
      }

      return merchantStore
    } catch {
      return null
    }

    // [client] causes endless api calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateCustomerId = async (customerId: string) => {
    if (!cartId) {
      return null
    }

    try {
      const result = await client.query({
        query: MUTATE_UPDATE_CUSTOMER_ID,
        variables: {
          cartId,
          customerId
        },
        fetchPolicy: 'no-cache'
      })

      const updateCart = result?.data?.updateCart

      if (!updateCart) {
        return null
      }

      return updateCart
    } catch {
      return null
    }
  }

  const fetchSlerpCart = async (cart: Cart | null) => {
    if (!cartId) {
      setIsSlerpCartLoading(false)
      return 'ok'
    }

    // Return cached fulfillment data if cart is same
    if (cart && cartStoreCache[cartId]) {
      const shortenedCart = returnShortenedCart(cart)

      if (isEqual(cartStoreCache[cartId][0], shortenedCart)) {
        setIsSlerpCartLoading(false)
        return cartStoreCache[cartId][1]
      }
    }

    setIsSlerpCartLoading(true)

    try {
      const result = await client.query({
        query: QUERY_GET_SLERP_CART,
        variables: {
          cartId: cart?.id || cartId
        },
        fetchPolicy: 'no-cache'
      })

      const slerpCart = result?.data?.getSlerpCart

      // Cache response against cartId
      if (cart && slerpCart) {
        const shortenedCart = returnShortenedCart(cart)
        setCartStoreCache({
          ...cartStoreCache,
          [cartId]: [shortenedCart, slerpCart]
        })
      }

      setIsSlerpCartLoading(false)

      if (!slerpCart) {
        return null
      }

      return slerpCart
    } catch {
      setIsSlerpCartLoading(false)

      return null
    }
  }

  const fetchSlerpMerchant = useCallback(async () => {
    if (!merchantSlug) {
      return null
    }

    try {
      const result = await client.query({
        query: QUERY_GET_SLERP_MERCHANT,
        variables: {
          merchantSlug
        }
      })

      const slerpMerchant = result?.data?.getMerchant

      if (!slerpMerchant) {
        return null
      }

      return slerpMerchant
    } catch {
      return null
    }

    // [client] causes endless api calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantSlug])

  return {
    fetchAppContent,
    fetchMerchantStore,
    fetchSlerpCart,
    fetchSlerpMerchant,
    updateCustomerId
  }
}

const defaultResponse = () => new Promise<null>((resolve) => resolve(null))

const initialState: LoaderState = {
  fetchAppContent: defaultResponse,
  fetchMerchantStore: defaultResponse,
  fetchSlerpCart: defaultResponse,
  fetchSlerpMerchant: defaultResponse,
  updateCustomerId: defaultResponse
}

export const LoaderContext = createContext<LoaderState>(initialState)

export const useLoader = () => {
  return useContext(LoaderContext)
}
